import AddCommentIcon from '@mui/icons-material/AddComment';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import videojs from 'video.js';
import {
  getMediaBasePath,
  shouldIncludeCredentialsInRequest,
} from '../../../commonUtils/images';

import Player from 'video.js/dist/types/player';
// @ts-ignore
import 'video.js/dist/video-js.css';
import { ResourceCategoryEnum } from '@protos/content_management/content.db_pb';
import {
  VideoComment,
  addVideoComment,
  createCommentId,
  deleteVideoComment,
  formatVideoTime,
  getVideoComments,
  resolveLoggedInUser,
} from './videoAnnotationStorage';

interface VideoPlayerProps {
  videoUrl: string;
  options?: any;
  onReady?: (player: Player) => void;
  resourceCategory?: ResourceCategoryEnum;
  allowUserControls?: boolean /** When false, play/pause stay available but seek, skip, and other controls are locked. Defaults to true. */;
  // enables the time-based comment/annotation feature
  annotationsEnabled?: boolean;
  // logged-in user stored with each comment; falls back to persist:root
  currentUser?: { id: string; name: string };
}

type YTPlayerInstance = {
  playVideo: () => void;
  pauseVideo: () => void;
  seekTo: (seconds: number, allowSeekAhead: boolean) => void;
  getCurrentTime: () => number;
  getPlaybackRate: () => number;
  getPlayerState: () => number;
  destroy: () => void;
};

type YTNamespace = {
  Player: new (
    el: HTMLElement | string,
    opts: {
      videoId: string;
      width?: string | number;
      height?: string | number;
      playerVars?: Record<string, number | string>;
      events?: {
        onReady?: (e: { target: YTPlayerInstance }) => void;
        onStateChange?: (e: { data: number; target: YTPlayerInstance }) => void;
      };
    }
  ) => YTPlayerInstance;
  PlayerState: {
    ENDED: number;
    PLAYING: number;
    PAUSED: number;
    BUFFERING: number;
    CUED: number;
  };
};

declare global {
  interface Window {
    YT?: YTNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

/** Extract an 11-char YouTube video id from common URL shapes. */
const extractYoutubeVideoId = (url: string): string | null => {
  if (!url) return null;
  const trimmed = url.trim();
  try {
    const parsed = new URL(
      /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
    );
    const host = parsed.hostname.replace(/^www\./, '');
    if (host === 'youtu.be') {
      const id = parsed.pathname.split('/').filter(Boolean)[0];
      return id?.slice(0, 11) || null;
    }
    if (
      host === 'youtube.com' ||
      host === 'm.youtube.com' ||
      host === 'youtube-nocookie.com'
    ) {
      if (parsed.pathname.startsWith('/embed/')) {
        return parsed.pathname.split('/')[2]?.slice(0, 11) || null;
      }
      if (parsed.pathname.startsWith('/shorts/')) {
        return parsed.pathname.split('/')[2]?.slice(0, 11) || null;
      }
      if (parsed.pathname.startsWith('/live/')) {
        return parsed.pathname.split('/')[2]?.slice(0, 11) || null;
      }
      const v = parsed.searchParams.get('v');
      if (v) return v.slice(0, 11);
    }
  } catch {
    // fall through to regex
  }
  const match = trimmed.match(
    /(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:embed\/|shorts\/|live\/|watch\?(?:.*&)?v=|.*[?&]v=))([A-Za-z0-9_-]{11})/
  );
  return match?.[1] ?? null;
};

const loadYoutubeIframeApi = (): Promise<YTNamespace> => {
  if (window.YT?.Player) {
    return Promise.resolve(window.YT);
  }
  return new Promise((resolve) => {
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previous?.();
      if (window.YT) resolve(window.YT);
    };
    if (
      !document.querySelector(
        'script[src="https://www.youtube.com/iframe_api"]'
      )
    ) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(tag);
    }
  });
};

/**
 * Minimal video.js-like adapter around YT.Player so VideoPlayerWrapper
 * analytics (play/pause/seek/timeupdate) and annotations keep working.
 */
const createYoutubePlayerAdapter = (
  ytPlayer: YTPlayerInstance,
  YT: YTNamespace
): Player => {
  const listeners = new Map<string, Set<(...args: any[]) => void>>();
  let timeupdateTimer: ReturnType<typeof setInterval> | null = null;
  let disposed = false;
  let lastState: number | null = null;

  const emit = (event: string) => {
    listeners.get(event)?.forEach((fn) => {
      try {
        fn();
      } catch {
        // ignore listener errors
      }
    });
  };

  const startTimeupdates = () => {
    if (timeupdateTimer) return;
    timeupdateTimer = setInterval(() => emit('timeupdate'), 250);
  };

  const stopTimeupdates = () => {
    if (timeupdateTimer) {
      clearInterval(timeupdateTimer);
      timeupdateTimer = null;
    }
  };

  const adapter = {
    on(event: string, fn: (...args: any[]) => void) {
      if (!listeners.has(event)) listeners.set(event, new Set());
      listeners.get(event)!.add(fn);
      return adapter;
    },
    off(event: string, fn?: (...args: any[]) => void) {
      if (!fn) {
        listeners.delete(event);
      } else {
        listeners.get(event)?.delete(fn);
      }
      return adapter;
    },
    play() {
      ytPlayer.playVideo();
      return Promise.resolve();
    },
    pause() {
      ytPlayer.pauseVideo();
    },
    paused() {
      const state = ytPlayer.getPlayerState();
      return (
        state !== YT.PlayerState.PLAYING && state !== YT.PlayerState.BUFFERING
      );
    },
    currentTime(seconds?: number) {
      if (typeof seconds === 'number') {
        emit('seeking');
        ytPlayer.seekTo(seconds, true);
        emit('seeked');
        return seconds;
      }
      return ytPlayer.getCurrentTime() || 0;
    },
    playbackRate() {
      return ytPlayer.getPlaybackRate?.() || 1;
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      stopTimeupdates();
      emit('dispose');
      listeners.clear();
      try {
        ytPlayer.destroy();
      } catch {
        // already destroyed
      }
    },
    // unused video.js APIs stubbed for safety
    controls() {
      return true;
    },
    addClass() {
      return adapter;
    },
    el() {
      return undefined;
    },
    getChild() {
      return undefined;
    },
  } as unknown as Player;

  // Wire YT state → video.js-like events
  (adapter as any).__handleStateChange = (data: number) => {
    if (disposed) return;
    if (data === YT.PlayerState.PLAYING) {
      startTimeupdates();
      emit('play');
    } else if (data === YT.PlayerState.PAUSED) {
      stopTimeupdates();
      emit('pause');
    } else if (data === YT.PlayerState.ENDED) {
      stopTimeupdates();
      emit('ended');
    } else if (data === YT.PlayerState.BUFFERING) {
      emit('waiting');
    }
    lastState = data;
  };

  void lastState;
  return adapter;
};

const VideoPlayer: React.FC<VideoPlayerProps> = (props) => {
  const videoReference = useRef<HTMLDivElement>(null);
  const youtubeHostRef = useRef<HTMLDivElement>(null);
  const playerReference = useRef<Player>();
  const {
    options,
    onReady,
    videoUrl,
    resourceCategory,
    allowUserControls = true,
    annotationsEnabled,
    currentUser,
  } = props;

  const youtubeVideoId = useMemo(
    () => extractYoutubeVideoId(videoUrl),
    [videoUrl]
  );
  const isYoutube = Boolean(youtubeVideoId);

  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [comments, setComments] = useState<VideoComment[]>([]);
  const [showComments, setShowComments] = useState(false);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [draftText, setDraftText] = useState('');
  const [pendingTime, setPendingTime] = useState(0);
  const wasPlayingRef = useRef(false);
  // Keep latest callbacks/options in refs so the YouTube mount effect does not
  // tear down/recreate the iframe on every parent re-render (e.g. PAL).
  const onReadyRef = useRef(onReady);
  const youtubeOptionsRef = useRef(options?.youtube);
  onReadyRef.current = onReady;
  youtubeOptionsRef.current = options?.youtube;

  const resolvedUser = useMemo(
    () => resolveLoggedInUser(currentUser),
    [currentUser]
  );

  // load stored comments for this video url
  useEffect(() => {
    if (annotationsEnabled && videoUrl) {
      setComments(getVideoComments(videoUrl));
    } else {
      setComments([]);
    }
  }, [annotationsEnabled, videoUrl]);

  const openAddComment = useCallback(() => {
    const player = playerReference.current;
    if (!player) return;
    wasPlayingRef.current = !player.paused();
    const time = player.currentTime() || 0;
    setPendingTime(time);
    player.pause();
    setDraftText('');
    setCommentDialogOpen(true);
  }, []);

  const resumePlayback = useCallback(() => {
    const player = playerReference.current;
    if (player && wasPlayingRef.current) {
      const maybePromise = player.play();
      if (maybePromise && typeof maybePromise.catch === 'function') {
        maybePromise.catch(() => undefined);
      }
    }
  }, []);

  const handleSubmitComment = useCallback(() => {
    const text = draftText.trim();
    if (!text) return;
    const comment: VideoComment = {
      id: createCommentId(),
      userId: resolvedUser.id,
      userName: resolvedUser.name,
      time: pendingTime,
      text,
      createdAt: Date.now(),
    };
    setComments(addVideoComment(videoUrl, comment));
    setCommentDialogOpen(false);
    setDraftText('');
    resumePlayback();
  }, [
    draftText,
    resolvedUser.id,
    resolvedUser.name,
    videoUrl,
    resumePlayback,
    pendingTime,
  ]);

  const handleCancelComment = useCallback(() => {
    setCommentDialogOpen(false);
    setDraftText('');
    resumePlayback();
  }, [resumePlayback]);

  const handleDeleteComment = useCallback(
    (commentId: string) => {
      setComments(deleteVideoComment(videoUrl, commentId));
    },
    [videoUrl]
  );

  const handleSeekTo = useCallback((time: number) => {
    const player = playerReference.current;
    if (!player) return;
    player.currentTime(time);
    const maybePromise = player.play();
    if (maybePromise && typeof maybePromise.catch === 'function') {
      maybePromise.catch(() => undefined);
    }
  }, []);

  /**
   * Restricts the player to play/pause only.
   * Seek, skip, playback-rate, and picture-in-picture stay locked so the
   * viewer cannot jump ahead or change how the video is played.
   */
  const lockUserControls = (player: Player) => {
    // Keep the control bar so play/pause remain visible.
    player.controls(true);
    player.addClass('vjs-no-user-controls');

    // Block PiP, which would otherwise let the viewer skip via native controls.
    const techEl = player.el()?.querySelector('video') as
      | HTMLVideoElement
      | undefined;
    if (techEl) {
      techEl.disablePictureInPicture = true;
    }

    // Hide skip / rate / PiP buttons; play/pause, volume, and fullscreen stay.
    const controlBar = player.getChild('controlBar');
    controlBar?.getChild('playbackRateMenuButton')?.hide();
    controlBar?.getChild('pictureInPictureToggle')?.hide();
    controlBar?.getChild('skipForward')?.hide();
    controlBar?.getChild('skipBackward')?.hide();

    // Remember the last played position so we can undo any seek.
    let lastTime = 0;
    player.on('timeupdate', () => {
      // seeking() is a real video.js Player API method; the installed
      // video.js/dist/types/player.d.ts just doesn't declare it.
      if (!(player as any).seeking()) {
        lastTime = player.currentTime() || 0;
      }
    });

    // Snap back if the user tries to scrub or skip via keyboard/native UI.
    player.on('seeking', () => {
      const nextTime = player.currentTime() || 0;
      if (Math.abs(nextTime - lastTime) > 0.5) {
        player.currentTime(lastTime);
      }
    });

    // Prevent right-click "Save video" / native menu shortcuts.
    player.on('contextmenu', (event: Event) => {
      event.preventDefault();
    });
  };

  // YouTube → native embed via IFrame API (not video.js / videojs-youtube)
  useEffect(() => {
    if (!isYoutube || !youtubeVideoId) return;

    let cancelled = false;
    let adapter: Player | undefined;
    setIsPlayerReady(false);

    const mount = async () => {
      const host = youtubeHostRef.current;
      if (!host) return;

      const YT = await loadYoutubeIframeApi();
      if (cancelled || !youtubeHostRef.current) return;

      // Clear previous embed if URL changed
      host.innerHTML = '';
      const mountEl = document.createElement('div');
      host.appendChild(mountEl);

      const ytPlayer = new YT.Player(mountEl, {
        videoId: youtubeVideoId,
        width: '100%',
        height: '100%',
        playerVars: {
          rel: 0,
          modestbranding: 1,
          playsinline: 1,
          ...(allowUserControls
            ? {}
            : {
                controls: 0,
                disablekb: 1,
                fs: 0,
              }),
          ...(youtubeOptionsRef.current || {}),
        },
        events: {
          onReady: (event) => {
            if (cancelled) return;
            adapter = createYoutubePlayerAdapter(event.target, YT);
            playerReference.current = adapter;
            setIsPlayerReady(true);
            onReadyRef.current?.(adapter);
          },
          onStateChange: (event) => {
            const handle = (adapter as any)?.__handleStateChange;
            if (typeof handle === 'function') {
              handle(event.data);
            }
          },
        },
      });

      // If onReady already fired before we assigned adapter handler, keep ref
      void ytPlayer;
    };

    mount();

    return () => {
      cancelled = true;
      setIsPlayerReady(false);
      if (playerReference.current) {
        try {
          playerReference.current.dispose();
        } catch {
          // ignore
        }
        playerReference.current = undefined;
      }
      if (youtubeHostRef.current) {
        youtubeHostRef.current.innerHTML = '';
      }
    };
    // Intentionally omit onReady / options — unstable parent callbacks were
    // remounting the iframe on every PAL re-render.
  }, [isYoutube, youtubeVideoId, allowUserControls]);

  const videoType = videoUrl.includes('m3u8')
    ? 'application/x-mpegURL'
    : 'video/mp4';

  // Non-YouTube → video.js
  useEffect(() => {
    if (isYoutube) return;

    // Initializing video.js player
    if (!playerReference.current) {
      const videoElement = document.createElement('video-js');
      videoElement.classList.add('vjs-big-play-centered');
      // Important: Ensure the video plays inline on iOS browsers
      videoElement.setAttribute('playsinline', '');
      videoReference.current?.appendChild(videoElement);
      const player = (playerReference.current = videojs(
        videoElement,
        {
          sources: [
            {
              src: getMediaBasePath(
                videoUrl,
                resourceCategory ===
                  ResourceCategoryEnum.RESOURCE_CATEGORY_MY_RESOURCES
                  ? 'userDataBucket'
                  : 'processedMediaBucket'
              ),
              type: videoType,
            },
          ],
          controls: true,
          autoplay: false,
          preload: 'auto',
          aspectRatio: '16:9',
          ...options,
          ...(allowUserControls
            ? {}
            : {
                controls: true,
                userActions: {
                  click: true,
                  doubleClick: false,
                  hotkeys: false,
                },
                disablePictureInPicture: true,
                controlBar: {
                  skipButtons: false,
                  playbackRateMenuButton: false,
                  pictureInPictureToggle: false,
                },
              }),
          html5: {
            // Additional VHS-specific options can go here.
            vhs: {
              // For example, to enable low latency streaming:
              overrideNative: true, // Force VHS to handle HLS playback even on platforms with native support.
              withCredentials: shouldIncludeCredentialsInRequest(
                getMediaBasePath(
                  videoUrl,
                  resourceCategory ===
                    ResourceCategoryEnum.RESOURCE_CATEGORY_MY_RESOURCES
                    ? 'userDataBucket'
                    : 'processedMediaBucket'
                )
              ),
            },
            nativeAudioTracks: false,
            nativeVideoTracks: false,
          },
        },
        () => {
          if (!allowUserControls) {
            lockUserControls(player);
          }
          setIsPlayerReady(true);
          onReady && onReady(player);
        }
      ));
    } else {
      const player = playerReference.current;
      player.autoplay(options.autoplay);
      player.src(options.sources);
    }
  }, [isYoutube, options, videoReference]);

  // Destroy video.js player on component unmount (non-YouTube only;
  // YouTube cleanup lives in its own effect)
  useEffect(() => {
    if (isYoutube) return;
    const player = playerReference.current;
    return () => {
      if (player) {
        player.dispose();
        playerReference.current = undefined;
      }
    };
  }, [isYoutube, playerReference]);

  if (!videoUrl) {
    return null;
  }

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {isYoutube ? (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            paddingTop: '56.25%', // 16:9
            backgroundColor: '#000',
            overflow: 'hidden',
          }}
        >
          {!isPlayerReady && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#000',
              }}
            >
              <CircularProgress size={48} sx={{ color: '#fff' }} />
            </Box>
          )}
          <Box
            ref={youtubeHostRef}
            data-id="resource_video_player"
            sx={{
              position: 'absolute',
              inset: 0,
              '& iframe': {
                width: '100%',
                height: '100%',
                border: 0,
              },
            }}
          />
        </Box>
      ) : (
        <div data-vjs-player data-id="resource_video_player">
          <div ref={videoReference} />
        </div>
      )}

      {annotationsEnabled && (
        <>
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 4,
              display: 'flex',
              gap: 0.5,
              backgroundColor: 'rgba(0,0,0,0.55)',
              borderRadius: 1,
            }}
          >
            <Tooltip title="Add comment">
              <span>
                <IconButton
                  size="small"
                  onClick={openAddComment}
                  disabled={!isPlayerReady}
                  sx={{ color: '#fff' }}
                >
                  <AddCommentIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="View comments">
              <span>
                <IconButton
                  size="small"
                  onClick={() => setShowComments((prev) => !prev)}
                  sx={{ color: '#fff' }}
                >
                  <Badge
                    badgeContent={comments.length}
                    color="primary"
                    max={99}
                  >
                    <ChatBubbleOutlineIcon fontSize="small" />
                  </Badge>
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {showComments && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                width: { xs: '100%', sm: 320 },
                maxWidth: '100%',
                zIndex: 5,
                backgroundColor: 'rgba(20,20,20,0.92)',
                color: '#fff',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                px={1.5}
                py={1}
                sx={{ borderBottom: '1px solid rgba(255,255,255,0.15)' }}
              >
                <Typography variant="subtitle1">Comments</Typography>
                <IconButton
                  size="small"
                  onClick={() => setShowComments(false)}
                  sx={{ color: '#fff' }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 1 }}>
                {comments.length === 0 ? (
                  <Typography variant="body2" sx={{ opacity: 0.7, mt: 1 }}>
                    No comments yet. Pause at a moment and add one.
                  </Typography>
                ) : (
                  comments.map((comment) => (
                    <Box
                      key={comment.id}
                      sx={{
                        py: 1,
                        borderBottom: '1px solid rgba(255,255,255,0.08)',
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="flex-start"
                        justifyContent="space-between"
                        gap={1}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            component="button"
                            onClick={() => handleSeekTo(comment.time)}
                            sx={{
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              color: '#90caf9',
                              cursor: 'pointer',
                              fontWeight: 600,
                              fontSize: '0.875rem',
                              textAlign: 'left',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            {formatVideoTime(comment.time)}
                          </Typography>
                          <Typography
                            component="span"
                            sx={{ ml: 0.75, fontSize: '0.875rem' }}
                          >
                            - {comment.text}
                          </Typography>
                          {comment.userName && (
                            <Typography
                              variant="caption"
                              display="block"
                              sx={{ opacity: 0.7, mt: 0.25 }}
                            >
                              {comment.userName}
                            </Typography>
                          )}
                        </Box>
                        {resolvedUser.id && comment.userId === resolvedUser.id && (
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteComment(comment.id)}
                            sx={{ color: 'rgba(255,255,255,0.7)' }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </Box>
          )}

          <Dialog
            open={commentDialogOpen}
            onClose={handleCancelComment}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>
              Add comment at {formatVideoTime(pendingTime)}
            </DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                fullWidth
                multiline
                minRows={2}
                placeholder="Write a comment…"
                value={draftText}
                onChange={(e) => setDraftText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmitComment();
                  }
                }}
                sx={{ mt: 1 }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCancelComment}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleSubmitComment}
                disabled={!draftText.trim()}
              >
                Submit
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default VideoPlayer;

// https://videojs.com/guides/react/
